import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Button, Card, Form, Spinner } from "react-bootstrap";
import { toast } from "react-toastify";

import {
  exportEventAttendeesCsv,
  getEvent,
  getEventSummary,
  listEventAttendees,
  updateEventAttendee,
} from "./services/api";

const STATUS_OPTIONS = [
  { value: "", label: "Todos" },
  { value: "pending", label: "Pendente" },
  { value: "confirmed", label: "Confirmado" },
  { value: "checked_in", label: "Check-in" },
  { value: "cancelled", label: "Cancelado" },
  { value: "refunded", label: "Reembolsado" },
];

const EDITABLE_STATUSES = [
  "pending",
  "confirmed",
  "checked_in",
  "cancelled",
  "refunded",
];

// Statuses where a check-in is unusual and deserves a confirmation prompt
// (the ticket isn't paid/active yet, or it was already invalidated).
const CHECKIN_WARN_STATUSES = {
  pending: "pendente (ainda não confirmado)",
  cancelled: "cancelado",
  refunded: "reembolsado",
};

function statusBadge(status) {
  const map = {
    pending: "bg-amber-100 text-amber-800 border-amber-200",
    confirmed: "bg-emerald-100 text-emerald-800 border-emerald-200",
    checked_in: "bg-blue-100 text-blue-800 border-blue-200",
    cancelled: "bg-slate-100 text-slate-600 border-slate-200",
    refunded: "bg-slate-100 text-slate-600 border-slate-200",
  };
  const label = {
    pending: "Pendente",
    confirmed: "Confirmado",
    checked_in: "Check-in",
    cancelled: "Cancelado",
    refunded: "Reembolsado",
  };
  const cls = map[status] || map.pending;
  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded-full border text-xs font-semibold uppercase tracking-wide ${cls}`}
    >
      {label[status] || status || "—"}
    </span>
  );
}

function formatDate(iso) {
  if (!iso) return "—";
  try {
    return new Intl.DateTimeFormat("pt-BR", {
      dateStyle: "short",
      timeStyle: "short",
    }).format(new Date(iso));
  } catch (_e) {
    return iso;
  }
}

export default function EventAttendees() {
  const navigate = useNavigate();
  const { eventId } = useParams();

  const [event, setEvent] = useState(null);
  const [summary, setSummary] = useState(null);
  const [rows, setRows] = useState([]);
  const [filterSearch, setFilterSearch] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [loading, setLoading] = useState(true);
  const [updatingId, setUpdatingId] = useState(null);
  const [exporting, setExporting] = useState(false);

  async function loadAll() {
    setLoading(true);
    const [evResp, sumResp, attResp] = await Promise.all([
      getEvent(eventId),
      getEventSummary(eventId),
      listEventAttendees(eventId, {
        status: filterStatus || undefined,
        search: filterSearch || undefined,
        limit: 500,
      }),
    ]);
    if (evResp instanceof Error) toast.error(evResp.message);
    else setEvent(evResp.data);
    if (sumResp instanceof Error) toast.error(sumResp.message);
    else setSummary(sumResp.data);
    if (attResp instanceof Error) toast.error(attResp.message);
    else setRows((attResp.data || []).filter((r) => !r.count));
    setLoading(false);
  }

  useEffect(() => {
    loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId]);

  function handleFilter() {
    loadAll();
  }

  function handleClear() {
    setFilterSearch("");
    setFilterStatus("");
    setTimeout(() => loadAll(), 0);
  }

  async function handleStatusChange(attendee, newStatus) {
    if (!EDITABLE_STATUSES.includes(newStatus)) return;
    if (attendee.status === newStatus) return;
    if (newStatus === "checked_in") {
      const reason = CHECKIN_WARN_STATUSES[attendee.status];
      if (reason) {
        const ok = window.confirm(
          `Este ticket está ${reason}. Tem certeza que deseja fazer o check-in mesmo assim?`
        );
        if (!ok) return;
      }
    }
    setUpdatingId(attendee.id);
    const resp = await updateEventAttendee(attendee.id, { status: newStatus });
    setUpdatingId(null);
    if (resp instanceof Error) {
      toast.error(resp.message);
      return;
    }
    toast.success(`Status atualizado para "${newStatus}"`);
    setRows((prev) =>
      prev.map((r) =>
        r.id === attendee.id
          ? {
              ...r,
              status: newStatus,
              checked_in_at:
                newStatus === "checked_in"
                  ? resp.data.checked_in_at || new Date().toISOString()
                  : r.checked_in_at,
            }
          : r
      )
    );
  }

  async function handleCheckIn(attendee) {
    await handleStatusChange(attendee, "checked_in");
  }

  async function handleExport() {
    setExporting(true);
    const resp = await exportEventAttendeesCsv(eventId, event?.slug);
    setExporting(false);
    if (resp instanceof Error) {
      toast.error(resp.message);
      return;
    }
    toast.success("CSV gerado");
  }

  return (
    <div className="w-full max-w-[1400px] mx-auto px-6 pb-8">
      <div className="w-full flex items-center justify-between py-6 border-b border-slate-200 mb-6">
        <div className="flex flex-col">
          <button
            type="button"
            onClick={() => navigate("/tools/events")}
            className="text-slate-500 text-xs uppercase tracking-wider hover:text-blue-600 bg-transparent border-0 p-0 text-left"
          >
            ← Eventos
          </button>
          <h1 className="text-slate-900 text-2xl font-bold tracking-tight m-0">
            Participantes ·{" "}
            <span className="font-mono text-blue-700">
              {event?.title || eventId}
            </span>
          </h1>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="primary"
            onClick={handleExport}
            disabled={exporting || loading}
          >
            {exporting ? (
              <>
                <Spinner size="sm" className="me-2" /> Exportando...
              </>
            ) : (
              "Exportar CSV"
            )}
          </Button>
          <Button
            variant="outline-primary"
            onClick={() => navigate(`/tools/events/${eventId}/registrations`)}
          >
            Ver inscrições
          </Button>
          <Button
            variant="outline-primary"
            onClick={() => navigate(`/tools/events/${eventId}/edit`)}
          >
            Editar evento
          </Button>
          <Button
            variant="outline-secondary"
            onClick={() => navigate("/tools/events")}
          >
            Voltar
          </Button>
        </div>
      </div>

      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
          <SummaryTile label="Total" value={summary.attendees_total} />
          <SummaryTile label="Pendentes" value={summary.attendees_pending} />
          <SummaryTile
            label="Confirmados"
            value={summary.attendees_confirmed}
          />
          <SummaryTile label="Check-in" value={summary.attendees_checked_in} />
          <SummaryTile
            label="Capacidade"
            value={
              summary.capacity
                ? `${summary.attendees_registered} / ${summary.capacity}`
                : `${summary.attendees_registered} / ∞`
            }
          />
        </div>
      )}

      <Card className="p-4 border-0 shadow-sm mb-6">
        <div className="flex flex-wrap items-end gap-4">
          <div className="flex flex-col gap-1 min-w-[260px] flex-1">
            <label className="text-xs text-slate-500 font-semibold uppercase tracking-wider">
              Buscar (nome, e-mail ou ticket code)
            </label>
            <Form.Control
              type="text"
              value={filterSearch}
              onChange={(e) => setFilterSearch(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") handleFilter();
              }}
            />
          </div>
          <div className="flex flex-col gap-1 min-w-[180px]">
            <label className="text-xs text-slate-500 font-semibold uppercase tracking-wider">
              Status
            </label>
            <Form.Select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
            >
              {STATUS_OPTIONS.map((s) => (
                <option key={s.value} value={s.value}>
                  {s.label}
                </option>
              ))}
            </Form.Select>
          </div>
          <div className="flex items-center gap-2 ml-auto">
            <Button variant="primary" onClick={handleFilter}>
              Filtrar
            </Button>
            {(filterSearch || filterStatus) && (
              <Button variant="outline-secondary" onClick={handleClear}>
                Limpar
              </Button>
            )}
          </div>
        </div>
      </Card>

      <Card className="border-0 shadow-sm overflow-hidden">
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200 bg-slate-50">
          <span className="text-sm font-semibold text-slate-700">
            {rows.length}{" "}
            {rows.length === 1 ? "participante" : "participantes"}
          </span>
        </div>
        {loading ? (
          <div className="w-full text-center py-10">
            <Spinner />
          </div>
        ) : rows.length === 0 ? (
          <div className="w-full text-center py-10 text-slate-500">
            Nenhum participante encontrado.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider w-12">
                    #
                  </th>
                  <th className="px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">
                    Ticket
                  </th>
                  <th className="px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">
                    Participante
                  </th>
                  <th className="px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">
                    Check-in
                  </th>
                  <th className="px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider text-right">
                    Ações
                  </th>
                </tr>
              </thead>
              <tbody>
                {rows.map((a, idx) => (
                  <tr
                    key={a.id}
                    className="border-b border-slate-100 hover:bg-slate-50 transition-colors"
                  >
                    <td className="px-4 py-3 text-sm text-slate-400 tabular-nums">
                      {idx + 1}
                    </td>
                    <td className="px-4 py-3 text-sm font-mono font-semibold text-slate-900">
                      {a.ticket_code}
                    </td>
                    <td className="px-4 py-3 text-sm">
                      <div className="font-semibold text-slate-900">
                        {a.name}
                      </div>
                      <div className="text-xs text-slate-500">{a.email}</div>
                      {a.phone && (
                        <div className="text-xs text-slate-500">{a.phone}</div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm">
                      {statusBadge(a.status)}
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-600 whitespace-nowrap">
                      {formatDate(a.checked_in_at)}
                    </td>
                    <td className="px-4 py-3 text-sm">
                      <div className="flex items-center gap-2 justify-end">
                        <Form.Select
                          size="sm"
                          value={a.status || "pending"}
                          onChange={(e) =>
                            handleStatusChange(a, e.target.value)
                          }
                          disabled={updatingId === a.id}
                          style={{ width: 160 }}
                        >
                          {EDITABLE_STATUSES.map((s) => (
                            <option key={s} value={s}>
                              {s}
                            </option>
                          ))}
                        </Form.Select>
                        {a.status !== "checked_in" && (
                          <Button
                            size="sm"
                            variant="outline-primary"
                            onClick={() => handleCheckIn(a)}
                            disabled={updatingId === a.id}
                          >
                            Check-in
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}

function SummaryTile({ label, value }) {
  return (
    <Card className="border-0 shadow-sm">
      <div className="px-4 py-3">
        <div className="text-xs text-slate-500 font-semibold uppercase tracking-wider">
          {label}
        </div>
        <div className="text-2xl font-bold text-slate-900 tabular-nums">
          {value}
        </div>
      </div>
    </Card>
  );
}
